import { directSupabase } from '../lib/supabaseClient';
import {
  filterRecordings,
  mapDirection,
  normalizeRecording,
  paginate,
  persistedRecordingToNormalized,
  presetRange,
  upsertByDedupeKey,
  type CallDirection,
  type DatePreset,
  type HuntGroupId,
  type NormalizedRecording,
  type RecordingUser,
} from './recordings.model';

export type ElevateListState = {
  rows: NormalizedRecording[];
  users: RecordingUser[];
  lastSyncAt: string | null;
  persisted: boolean;
  disclaimer: string;
};

async function accessToken(): Promise<string> {
  const { data } = await directSupabase.auth.getSession();
  const token = data.session?.access_token || localStorage.getItem('sb-access-token') || '';
  if (!token) throw new Error('Sign in required');
  return token;
}

export async function syncElevateRecordings(refresh = true): Promise<ElevateListState> {
  const token = await accessToken();
  const url = `/api/elevate/recordings${refresh ? '?refresh=1' : ''}`;
  const resp = await fetch(url, {
    method: refresh ? 'POST' : 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await resp.json().catch(() => ({}));
  if (resp.status === 503 && data.code === 'not_configured') {
    const error = new Error(data.error || 'Elevate is not configured');
    (error as Error & { code?: string }).code = 'not_configured';
    throw error;
  }
  if (resp.status === 401 || resp.status === 403) {
    const error = new Error(data.error || 'Not permitted');
    (error as Error & { code?: string }).code = 'auth';
    throw error;
  }
  if (!resp.ok) {
    const error = new Error(data.error || 'Elevate request failed');
    (error as Error & { code?: string }).code = data.code || 'elevate_upstream';
    throw error;
  }

  const users: RecordingUser[] = Array.isArray(data.users) ? data.users : [];
  const mapped = upsertByDedupeKey(
    (Array.isArray(data.recordings) ? data.recordings : [])
      .map((row: { owner?: RecordingUser; raw?: Record<string, unknown> }) => {
        if (!row?.raw || !row.owner) return null;
        return normalizeRecording(row.raw, row.owner);
      })
      .filter(Boolean) as NormalizedRecording[],
  );

  return {
    rows: mapped,
    users,
    lastSyncAt: data.lastSyncAt || null,
    persisted: Boolean(data.persisted),
    disclaimer: String(data.disclaimer || ''),
  };
}

export function queryRecordings(
  rows: NormalizedRecording[],
  input: {
    preset: DatePreset;
    nowIso: string;
    customFrom?: string;
    customTo?: string;
    query?: string;
    direction?: CallDirection | 'all';
    ownerUserId?: string | 'all';
    huntGroup?: HuntGroupId | 'all';
    extraSearchText?: (row: NormalizedRecording) => string;
    page: number;
    pageSize?: number;
  },
) {
  const range = presetRange(input.preset, input.nowIso, input.customFrom, input.customTo);
  const filtered = filterRecordings(rows, {
    fromDay: range.fromDay,
    toDay: range.toDay,
    query: input.query,
    direction: input.direction,
    ownerUserId: input.ownerUserId,
    huntGroup: input.huntGroup,
    extraSearchText: input.extraSearchText,
  });
  return { ...paginate(filtered, input.page, input.pageSize || 20), filtered };
}

export async function loadPersistedRecordings(): Promise<NormalizedRecording[]> {
  const { data, error } = await directSupabase
    .from('elevate_call_recordings')
    .select('*')
    .order('started_at', { ascending: false });
  if (error) {
    const text = String(error.message || '');
    if (error.code === '42P01' || text.includes('elevate_call_recordings')) return [];
    throw new Error(error.message);
  }
  return upsertByDedupeKey(
    (data || [])
      .map((row) => persistedRecordingToNormalized(row as Record<string, unknown>))
      .filter((row): row is NormalizedRecording => Boolean(row)),
  );
}

export function isRecordingAudioUnavailable(code?: string): boolean {
  return code === 'expired' || code === 'archive_failed' || code === 'unavailable';
}

export async function fetchRecordingAudio(ownerUserId: string, recordingId: string, download = false): Promise<Blob> {
  const token = await accessToken();
  const resp = await fetch(
    `/api/elevate/audio?ownerUserId=${encodeURIComponent(ownerUserId)}&recordingId=${encodeURIComponent(recordingId)}${download ? '&download=1' : ''}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (resp.status === 404) {
    const data = await resp.json().catch(() => ({}));
    const error = new Error(data.error || 'This recording is no longer available.');
    (error as Error & { code?: string }).code = data.code || 'expired';
    throw error;
  }
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data.error || 'Could not load audio');
  }
  return resp.blob();
}
